import { useState, useEffect, useMemo } from 'react';
import { formatGBDatestringToGBIsoDateTime } from "../utils/dateUtils";

/**
 * Custom hook for filtering data by specific fields and their values, including date range.
 * When a filter value is "" or "all", that field is ignored.
 * @param {Array} initialData - The initial data to be filtered.
 * @returns {Array} - A triple containing the filtered data, a function to update the filters and a function to remove filters.
 */
export function useFilterDataByField(initialData) {
    const [filters, setFilters] = useState({});

    const filterItems = (items, filters) => {
        return items.filter(item => {
            // Check if all filter conditions match
            return Object.entries(filters).every(([field, value]) => {
                // If the filter value is "" or "all", ignore filtering for this field.
                if (value === "" || value === "all") {
                    return true;
                }
                if (typeof value === "function") {
                     return value(item)
                }

                // Handle empty object value filtering (exclude records with the field)
                if (value && typeof value === "object" && Object.keys(value).length === 0) {
                    return item[field] === undefined || item[field] === null;
                }

                // Handle date range filtering
                if (value instanceof Object && (value.hasOwnProperty("from") || value.hasOwnProperty("to"))) {
                    const fieldDate = formatGBDatestringToGBIsoDateTime(item[field]);
                    const fromDate = value.from ? new Date(value.from) : null;
                    const toDate = value.to ? new Date(value.to) : null;

                    const isAfterFrom = !fromDate || fieldDate >= fromDate;
                    const isBeforeTo = !toDate || fieldDate <= toDate;
                    return isAfterFrom && isBeforeTo;
                }

                // Handle null value filtering
                if (value === null) {
                    return item[field] === null || item[field] === undefined;
                }

                // Standard value filtering (case-insensitive)
                const itemValue = String(item[field] || "").toLowerCase();
                const filterValue = String(value).toLowerCase();

                // If the parent item already matches, no need to check child items.
                if (itemValue === filterValue) {
                    return true;
                }

                const hasChildItems = Array.isArray(item.items);
                const childItemMatchesFilter = hasChildItems && item.items.some(subItem => {
                    const subItemValue = String(subItem[field] || "").toLowerCase();
                    return subItemValue === filterValue;
                });

                return childItemMatchesFilter;
            });
        });
    }

    // Function to filter data based on the specified field-value pairs
    const filterData = (data, filters) => {
        const filteredTopLevelItems = filterItems(data, filters);

        // For items with sub-items, filter their sub-items as well
        return filteredTopLevelItems.map(item => {
            if (item.items && Array.isArray(item.items)) {
                const filteredSubItems = filterItems(item.items, filters);
                return { ...item, items: filteredSubItems };
            }
            return item;
        });
    };

    // Filtered data based on the specified field-value pairs
    const filteredData = useMemo(() => filterData(initialData, filters), [initialData, filters]);

    // Function to update the filters
    const setFilter = (field, value) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [field]: typeof value === "object" ? { ...prevFilters[field], ...value } : value
        }));
    };

    // Function to remove a filter
    const removeFilter = field => {
        setFilters(prevFilters => {
            const { [field]: omit, ...restFilters } = prevFilters;
            return restFilters;
        });
    };

    useEffect(() => {
        // No-op: hook re-runs when filters or filteredData change.
    }, [filters, filteredData]);

    return [filteredData, setFilter, removeFilter];
}